import { ParkTimeContractType, type DateRange, type ParkTimeContract } from '@repo/domain';
import {
  addMinutes,
  differenceInMinutes,
  startOfDay,
  startOfToday,
  MINUTES_IN_HOUR,
  MINUTES_IN_MONTH,
} from '@repo/date-time';

import { MINIMUM_MINUTES_PICKABLE } from '@shared/constants/timeContract';
import { MAX_RESERVATION_DURATION_MONTHS } from '@shared/constants/reservation';

export const DEFAULT_PARK_DURATION = 60 as const;
export const getToday = (): Date => startOfToday();

const MAXIMUM_MINUTES_PICKABLE: number = MAX_RESERVATION_DURATION_MONTHS * MINUTES_IN_MONTH;

export const clampValue = (value: number, minimum: number, maximum: number): number => {
  return Math.max(minimum, Math.min(maximum, value));
};

export const parseInputValue = (value: string): number => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? 0 : Math.floor(parsed);
};

export const toDurationMinutes = (hours: number, minutes: number): number => {
  const safeHours = Math.max(0, hours) + Math.floor(Math.max(0, minutes) / MINUTES_IN_HOUR);
  const safeMinutes = Math.max(0, minutes) % MINUTES_IN_HOUR;
  const totalMinutes = safeHours * MINUTES_IN_HOUR + safeMinutes;

  return clampValue(totalMinutes, MINIMUM_MINUTES_PICKABLE, MAXIMUM_MINUTES_PICKABLE);
};

// contract --> duration
export const calculateParkDurationFromContract = (contract: ParkTimeContract): number => {
  if (contract.type === ParkTimeContractType.FixedDuration) {
    return contract.duration;
  }

  const startAt = contract.startAtTimestamp ?? Date.now();
  return differenceInMinutes(contract.endAtTimestamp, startAt);
};

// contract --> slider value (mode-dependent: duration for FixedDuration, end time-of-day for FixedEnd)
export const calculateSliderValueFromContract = (contract: ParkTimeContract): number => {
  if (contract.type === ParkTimeContractType.FixedDuration) {
    return contract.duration;
  }

  const endAt = new Date(contract.endAtTimestamp);
  return differenceInMinutes(endAt, startOfDay(endAt));
};

export const calculateDateRangeFromContract = (contract: ParkTimeContract, startAt: Date): DateRange => {
  const startDay = startOfDay(startAt);
  if (contract.type === ParkTimeContractType.FixedDuration) {
    return [startDay, startDay];
  }
  return [startDay, startOfDay(new Date(contract.endAtTimestamp))];
};

export const createTimeContract = (
  contractType: ParkTimeContractType,
  selectedDate: Date,
  selectedMinutes: number
): ParkTimeContract => {
  return contractType === ParkTimeContractType.FixedDuration
    ? { type: ParkTimeContractType.FixedDuration, duration: selectedMinutes }
    : {
        type: ParkTimeContractType.FixedEnd,
        endAtTimestamp: addMinutes(startOfDay(selectedDate), selectedMinutes).getTime(),
      };
};

export const resolveParkingTime = (
  timeContract: ParkTimeContractType,
  selectedDate: Date,
  selectedMinutes: number,
  startAt: Date
): { duration: number; parkingEndAt: Date } => {
  const parkingEndAt: Date =
    timeContract === ParkTimeContractType.FixedDuration
      ? addMinutes(startAt, selectedMinutes)
      : addMinutes(selectedDate, selectedMinutes);
  const duration: number = differenceInMinutes(parkingEndAt, startAt);

  return {
    parkingEndAt,
    duration,
  };
};
